import { makeInitialCallModel } from "../domain/callModel.js";
import { CONTROLLER_COMMAND_TYPE as CCT } from "../domain/controllerCommands.js";
import { CONTROLLER_EVENT_TYPE as CET } from "../domain/controllerEvents.js";
import { reduceCallModel } from "../domain/reducer.js";
import { selectActiveCallId } from "../domain/selectors.js";
import { SIGNALING_ADAPTER_EVENT_TYPE as SAET } from "../adapters/signalingAdapter.js";
import { MEDIA_ADAPTER_EVENT_TYPE as MAET } from "../adapters/mediaAdapter.js";
import { PEER_CONNECTION_ADAPTER_EVENT_TYPE as PCAET } from "../adapters/peerConnectionAdapter.js";
import { mapServerToClientFrameToEvent, makeAckFrame, makeCallAcceptFrame, makeCallCreateFrame, makeCallEndFrame, makeCallRejectFrame, makeHelloFrame, makeIceCandidateFrame, makePcConnectionStateFrame, makePingFrame, makeSdpAnswerFrame, makeSdpOfferFrame, } from "../protocol/mappers.js";
import { PROTOCOL_EVENT_TYPE as PET } from "../protocol/protocolEventTypes.js";
import { CALL_ROLE } from "../domain/callDirections.js";
import { allocateOutboundFrameMeta, makeInitialSessionRuntime, noteServerSeqSeen, noteSessionId, peekOutboundFrameMeta, } from "../runtime/sessionRuntime.js";
export class DefaultCallController {
    constructor(deps) {
        this.stateListeners = new Set();
        this.eventListeners = new Set();
        this.wsMessageLogListeners = new Set();
        this.unsubscribeFns = [];
        this.wsMessageLog = [];
        this.pendingRemoteIceByCallId = new Map();
        this.state = makeInitialCallModel();
        this.signalingAdapter = deps.signalingAdapter;
        this.mediaAdapter = deps.mediaAdapter;
        this.peerConnectionAdapter = deps.peerConnectionAdapter;
        this.signalingUrl = deps.signalingUrl;
        this.protocolVersion = deps.protocolVersion ?? 1;
        this.rtcConfigProvider = deps.rtcConfigProvider;
        this.now = deps.now ?? (() => Date.now());
        this.makeMsgId =
            deps.makeMsgId ??
                (() => `m_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`);
        this.allowServerCallStateSnapshots = deps.allowServerCallStateSnapshots ?? true;
        this.runtime = makeInitialSessionRuntime();
        this.wireAdapters();
    }
    getState() {
        return this.state;
    }
    subscribe(listener) {
        this.stateListeners.add(listener);
        return () => {
            this.stateListeners.delete(listener);
        };
    }
    subscribeEvents(listener) {
        this.eventListeners.add(listener);
        return () => {
            this.eventListeners.delete(listener);
        };
    }
    getWsMessageLog() {
        return [...this.wsMessageLog];
    }
    getResumeSnapshot() {
        return {
            sessionId: this.runtime.lastKnownSessionId ?? null,
            lastServerSeqSeen: Number(this.runtime.lastServerSeqSeen ?? 0) || 0,
        };
    }
    hydrateResumeSnapshot(snapshot) {
        if (!snapshot || typeof snapshot !== "object") {
            return;
        }
        const hasSessionId = typeof snapshot.sessionId === "string" && snapshot.sessionId.trim();
        const lastServerSeqSeen = Number(snapshot.lastServerSeqSeen);
        this.runtime = {
            ...this.runtime,
            ...(hasSessionId ? { lastKnownSessionId: snapshot.sessionId } : {}),
            ...(Number.isFinite(lastServerSeqSeen) && lastServerSeqSeen >= 0
                ? { lastServerSeqSeen }
                : {}),
        };
    }
    subscribeWsMessageLog(listener) {
        this.wsMessageLogListeners.add(listener);
        return () => {
            this.wsMessageLogListeners.delete(listener);
        };
    }
    async dispose() {
        this.wsMessageLogListeners.clear();
        for (const unsubscribe of this.unsubscribeFns.splice(0)) {
            try {
                unsubscribe();
            }
            catch {
                // best effort
            }
        }
        await Promise.allSettled([
            this.peerConnectionAdapter.closePeerConnection(),
            this.mediaAdapter.stopLocalMedia(),
            this.signalingAdapter.disconnect(),
        ]);
    }
    applyEvent(event) {
        this.state = reduceCallModel(this.state, event);
        if (event.type === CET.WELCOME_RECEIVED) {
            this.runtime = noteSessionId(this.runtime, event.payload.sessionId);
        }
        this.emitEvent(event);
        this.emitState();
    }
    async dispatch(command) {
        try {
            switch (command.type) {
                case CCT.CONNECT_SIGNALING:
                    return await this.handleConnectSignaling(command);
                case CCT.DISCONNECT_SIGNALING:
                    await this.signalingAdapter.disconnect();
                    return { ok: true };
                case CCT.SEND_PING:
                    return await this.sendFrame(makePingFrame(this.makeBaseOutboundFrameInput()));
                case CCT.SEND_ACK:
                    return await this.sendFrame(makeAckFrame(this.makeBaseOutboundFrameInput()));
                case CCT.CREATE_CALL:
                    return await this.sendFrame(makeCallCreateFrame({
                        ...this.makeBaseOutboundFrameInput(),
                        calleeUserId: command.calleeUserId,
                        ...(command.media !== undefined ? { media: command.media } : {}),
                        ...(command.features !== undefined ? { features: command.features } : {}),
                    }));
                case CCT.ACCEPT_CALL:
                    return await this.sendFrame(makeCallAcceptFrame({
                        ...this.makeBaseOutboundFrameInput(),
                        callId: command.callId,
                    }));
                case CCT.REJECT_CALL:
                    return await this.sendFrame(makeCallRejectFrame({
                        ...this.makeBaseOutboundFrameInput(),
                        callId: command.callId,
                    }));
                case CCT.END_CALL:
                    return await this.sendFrame(makeCallEndFrame({
                        ...this.makeBaseOutboundFrameInput(),
                        callId: command.callId,
                        ...(command.reason !== undefined ? { reason: command.reason } : {}),
                    }));
                case CCT.ACQUIRE_LOCAL_MEDIA:
                    return await this.handleAcquireLocalMedia(command);
                case CCT.STOP_LOCAL_MEDIA:
                    await this.mediaAdapter.stopLocalMedia();
                    return { ok: true };
                case CCT.TOGGLE_LOCAL_AUDIO:
                    await this.mediaAdapter.setLocalAudioEnabled(command.enabled);
                    this.applyEvent({
                        type: CET.LOCAL_AUDIO_ENABLED_CHANGED,
                        enabled: command.enabled,
                    });
                    return { ok: true };
                case CCT.TOGGLE_LOCAL_VIDEO:
                    await this.mediaAdapter.setLocalVideoEnabled(command.enabled);
                    this.applyEvent({
                        type: CET.LOCAL_VIDEO_ENABLED_CHANGED,
                        enabled: command.enabled,
                    });
                    return { ok: true };
                case CCT.SELECT_AUDIO_INPUT_DEVICE:
                    return await this.handleSelectAudioInputDevice(command.deviceId);
                case CCT.SELECT_VIDEO_INPUT_DEVICE:
                    return await this.handleSelectVideoInputDevice(command.deviceId);
                case CCT.SELECT_AUDIO_OUTPUT_DEVICE:
                    await this.mediaAdapter.selectAudioOutputDevice(command.deviceId);
                    return { ok: true };
                case CCT.REFRESH_MEDIA_DEVICES:
                    return await this.handleRefreshMediaDevices();
                case CCT.ENSURE_PEER_CONNECTION:
                    return await this.handleEnsurePeerConnection();
                case CCT.CLOSE_PEER_CONNECTION:
                    await this.peerConnectionAdapter.closePeerConnection();
                    return { ok: true };
                case CCT.CREATE_AND_SEND_OFFER:
                    return await this.handleCreateAndSendOffer(command.callId);
                case CCT.APPLY_REMOTE_OFFER_AND_SEND_ANSWER:
                    return await this.handleApplyRemoteOfferAndSendAnswer(command.callId, command.sdp);
                case CCT.APPLY_REMOTE_ANSWER:
                    return await this.handleApplyRemoteAnswer(command.callId, command.sdp);
                case CCT.ADD_REMOTE_ICE_CANDIDATE:
                    return await this.handleAddRemoteIceCandidate(command.callId, command.candidate);
                case CCT.SEND_LOCAL_ICE_CANDIDATE:
                    return await this.sendFrame(makeIceCandidateFrame({
                        ...this.makeBaseOutboundFrameInput(),
                        callId: command.callId,
                        candidate: command.candidate,
                    }));
                case CCT.REPORT_PC_CONNECTION_STATE:
                    return await this.sendFrame(makePcConnectionStateFrame({
                        ...this.makeBaseOutboundFrameInput(),
                        callId: command.callId,
                        connectionState: command.connectionState,
                    }));
                default: {
                    const _exhaustive = command;
                    return _exhaustive;
                }
            }
        }
        catch (err) {
            return {
                ok: false,
                error: err instanceof Error ? err.message : String(err),
            };
        }
    }
    wireAdapters() {
        const unsubTransport = this.signalingAdapter.subscribeTransport((event) => {
            this.handleSignalingTransportEvent(event);
        });
        const unsubFrames = this.signalingAdapter.subscribeFrames((frame) => {
            this.runtime = noteServerSeqSeen(this.runtime, frame.seq);
            this.appendWsMessageLog("in", frame);
            const protocolEvent = mapServerToClientFrameToEvent(frame);
            this.handleProtocolEvent(protocolEvent);
            // Keep ACK explicit and visible at the controller boundary.
            void this.dispatch({ type: CCT.SEND_ACK });
        });
        const unsubMedia = this.mediaAdapter.subscribe((event) => {
            this.handleMediaAdapterEvent(event);
        });
        const unsubPc = this.peerConnectionAdapter.subscribe((event) => {
            this.handlePeerConnectionAdapterEvent(event);
        });
        this.unsubscribeFns.push(unsubTransport, unsubFrames, unsubMedia, unsubPc);
    }
    handleSignalingTransportEvent(event) {
        switch (event.type) {
            case SAET.CONNECTING:
                this.applyEvent({ type: CET.SIGNALING_CONNECT_STARTED });
                return;
            case SAET.OPEN:
                this.applyEvent({ type: CET.SIGNALING_TRANSPORT_OPENED });
                return;
            case SAET.CLOSED:
                this.applyEvent({
                    type: CET.SIGNALING_TRANSPORT_CLOSED,
                    ...(event.code !== undefined ? { code: event.code } : {}),
                    ...(event.reason !== undefined ? { reason: event.reason } : {}),
                });
                return;
            case SAET.ERROR:
                return;
            default: {
                const _exhaustive = event;
                return _exhaustive;
            }
        }
    }
    handleProtocolEvent(event) {
        switch (event.type) {
            case PET.WELCOME_RECEIVED:
                this.applyEvent({
                    type: CET.WELCOME_RECEIVED,
                    payload: event.payload,
                });
                return;
            case PET.ERROR_RECEIVED:
                this.applyEvent({
                    type: CET.ERROR_RECEIVED,
                    payload: event.payload,
                    ...(event.frame.callId !== undefined ? { callId: event.frame.callId } : {}),
                });
                return;
            case PET.PONG_RECEIVED:
                this.applyEvent({ type: CET.PONG_RECEIVED });
                return;
            case PET.CALL_CREATED_RECEIVED:
                this.applyEvent({
                    type: CET.CALL_CREATED_RECEIVED,
                    callId: event.callId,
                    payload: event.payload,
                    observedAtTs: this.now()
                });
                return;
            case PET.CALL_INCOMING_RECEIVED:
                this.applyEvent({
                    type: CET.CALL_INCOMING_RECEIVED,
                    callId: event.callId,
                    payload: event.payload,
                    observedAtTs: this.now()
                });
                return;
            case PET.CALL_ACCEPTED_RECEIVED:
                this.applyEvent({
                    type: CET.CALL_ACCEPTED_RECEIVED,
                    callId: event.callId,
                    payload: event.payload,
                    observedAtTs: this.now()
                });
                // Convention for this prototype:
                // caller initiates SDP offer after server confirms acceptance.
                if (this.state.activeCall?.callId === event.callId &&
                    this.state.activeCall.selfRole === CALL_ROLE.CALLER) {
                    void this.dispatch({
                        type: CCT.CREATE_AND_SEND_OFFER,
                        callId: event.callId,
                    });
                }
                return;
            case PET.CALL_RECONNECTING_RECEIVED:
                this.applyEvent({
                    type: CET.CALL_RECONNECTING_RECEIVED,
                    callId: event.callId,
                    payload: event.payload,
                    observedAtTs: this.now()
                });
                return;
            case PET.CALL_IN_CALL_RECEIVED:
                this.applyEvent({
                    type: CET.CALL_IN_CALL_RECEIVED,
                    callId: event.callId,
                    observedAtTs: this.now()
                });
                return;
            case PET.CALL_RESUMED_RECEIVED:
                this.applyEvent({
                    type: CET.CALL_RESUMED_RECEIVED,
                    callId: event.callId,
                    observedAtTs: this.now()
                });
                return;
            case PET.CALL_ENDED_RECEIVED:
                this.applyEvent({
                    type: CET.CALL_ENDED_RECEIVED,
                    callId: event.callId,
                    payload: event.payload,
                    observedAtTs: this.now()
                });
                void this.dispatch({ type: CCT.CLOSE_PEER_CONNECTION });
                return;
            case PET.SDP_OFFER_RECEIVED:
                this.applyEvent({
                    type: CET.SDP_OFFER_RECEIVED,
                    callId: event.callId,
                    payload: event.payload,
                });
                void this.dispatch({
                    type: CCT.APPLY_REMOTE_OFFER_AND_SEND_ANSWER,
                    callId: event.callId,
                    sdp: event.payload.sdp,
                });
                return;
            case PET.SDP_ANSWER_RECEIVED:
                this.applyEvent({
                    type: CET.SDP_ANSWER_RECEIVED,
                    callId: event.callId,
                    payload: event.payload,
                });
                void this.dispatch({
                    type: CCT.APPLY_REMOTE_ANSWER,
                    callId: event.callId,
                    sdp: event.payload.sdp,
                });
                return;
            case PET.ICE_CANDIDATE_RECEIVED:
                this.applyEvent({
                    type: CET.ICE_CANDIDATE_RECEIVED,
                    callId: event.callId,
                    payload: event.payload,
                });
                void this.dispatch({
                    type: CCT.ADD_REMOTE_ICE_CANDIDATE,
                    callId: event.callId,
                    candidate: event.payload.candidate,
                });
                return;
            case PET.CALL_STATE_RECEIVED: {
                if (!this.allowServerCallStateSnapshots) {
                    return;
                }
                const previousActiveCallId = selectActiveCallId(this.state);
                this.applyEvent({
                    type: CET.CALL_STATE_RECEIVED,
                    callId: event.callId,
                    payload: event.payload,
                });
                const nextActiveCallId = selectActiveCallId(this.state);
                if (previousActiveCallId !== nextActiveCallId) {
                    void this.dispatch({ type: CCT.CLOSE_PEER_CONNECTION });
                }
                return;
            }
            default: {
                const _exhaustive = event;
                return _exhaustive;
            }
        }
    }
    handleMediaAdapterEvent(event) {
        switch (event.type) {
            case MAET.DEVICES_CHANGED:
                this.applyEvent({
                    type: CET.MEDIA_DEVICES_UPDATED,
                    devices: event.devices,
                });
                return;
            case MAET.SPEAKING_CHANGED:
                this.applyEvent({
                    type: CET.LOCAL_SPEAKING_DETECTED,
                    isSpeaking: event.isSpeaking,
                });
                return;
            default: {
                const _exhaustive = event;
                return _exhaustive;
            }
        }
    }
    handlePeerConnectionAdapterEvent(event) {
        switch (event.type) {
            case PCAET.CONNECTION_STATE_CHANGED: {
                this.applyEvent({
                    type: CET.RTC_CONNECTION_STATE_CHANGED,
                    connectionState: event.connectionState,
                    shouldReportToServer: true,
                });
                const callId = selectActiveCallId(this.state);
                if (callId) {
                    void this.dispatch({
                        type: CCT.REPORT_PC_CONNECTION_STATE,
                        callId,
                        connectionState: event.connectionState,
                    });
                }
                return;
            }
            case PCAET.ICE_GATHERING_STATE_CHANGED:
                this.applyEvent({
                    type: CET.RTC_ICE_GATHERING_STATE_CHANGED,
                    iceGatheringState: event.iceGatheringState,
                });
                return;
            case PCAET.NEGOTIATION_NEEDED:
                this.applyEvent({
                    type: CET.RTC_NEGOTIATION_NEEDED_CHANGED,
                    isNegotiationNeeded: true,
                });
                return;
            case PCAET.LOCAL_ICE_CANDIDATE: {
                const callId = selectActiveCallId(this.state);
                if (callId) {
                    void this.dispatch({
                        type: CCT.SEND_LOCAL_ICE_CANDIDATE,
                        callId,
                        candidate: {
                            candidate: event.candidate.candidate,
                            ...(event.candidate.sdpMid != null
                                ? { sdpMid: event.candidate.sdpMid }
                                : {}),
                            ...(event.candidate.sdpMLineIndex != null
                                ? { sdpMLineIndex: event.candidate.sdpMLineIndex }
                                : {}),
                        },
                    });
                }
                return;
            }
            case PCAET.REMOTE_MEDIA_UPDATED:
                this.applyEvent({
                    type: CET.RTC_REMOTE_MEDIA_UPDATED,
                    hasRemoteStream: event.remoteMedia.hasRemoteStream,
                    hasRemoteAudioTrack: event.remoteMedia.hasRemoteAudioTrack,
                    hasRemoteVideoTrack: event.remoteMedia.hasRemoteVideoTrack,
                });
                return;
            case PCAET.ERROR:
                this.applyEvent({
                    type: CET.RTC_ERROR_OCCURRED,
                    payload: {
                        code: event.error.code,
                        message: event.error.message,
                    },
                });
                return;
            default: {
                const _exhaustive = event;
                return _exhaustive;
            }
        }
    }
    async handleConnectSignaling(command) {
        const connectInput = {
            url: this.signalingUrl,
        };
        if (command.resume) {
            this.applyEvent({ type: CET.SIGNALING_RESUME_STARTED });
        }
        await this.signalingAdapter.connect(connectInput);
        return await this.sendFrame(makeHelloFrame({
            ...peekOutboundFrameMeta({
                runtime: this.runtime,
                protocolVersion: this.protocolVersion,
                now: this.now(),
                msgId: this.makeMsgId(),
            }),
            token: command.token,
            deviceId: command.deviceId,
            ...(command.role !== undefined ? { role: command.role } : {}),
            ...(command.resume !== undefined ? { resume: command.resume } : {}),
            ...(command.clientInfo !== undefined ? { clientInfo: command.clientInfo } : {}),
        }));
    }
    async handleAcquireLocalMedia(command) {
        this.applyEvent({
            type: CET.MEDIA_ACQUIRE_STARTED,
            requestedAudio: command.audio,
            requestedVideo: command.video,
        });
        const result = await this.mediaAdapter.acquireLocalMedia({
            audio: command.audio,
            video: command.video,
            ...(command.audioInputDeviceId !== undefined
                ? { audioInputDeviceId: command.audioInputDeviceId }
                : {}),
            ...(command.videoInputDeviceId !== undefined
                ? { videoInputDeviceId: command.videoInputDeviceId }
                : {}),
        });
        if (!result.ok) {
            this.applyEvent({
                type: CET.MEDIA_ACQUIRE_FAILED,
                payload: result.error,
            });
            return { ok: false, error: result.error.message };
        }
        this.applyEvent({
            type: CET.MEDIA_ACQUIRE_SUCCEEDED,
            hasAudioTrack: result.hasAudioTrack,
            hasVideoTrack: result.hasVideoTrack,
        });
        const syncResult = await this.syncPeerConnectionLocalMedia();
        if (!syncResult.ok) {
            return syncResult;
        }
        return { ok: true };
    }
    async handleSelectAudioInputDevice(deviceId) {
        const result = await this.mediaAdapter.selectAudioInputDevice(deviceId);
        if (!result.ok) {
            this.applyEvent({
                type: CET.MEDIA_ACQUIRE_FAILED,
                payload: result.error,
            });
            return { ok: false, error: result.error.message };
        }
        this.applyEvent({
            type: CET.MEDIA_ACQUIRE_SUCCEEDED,
            hasAudioTrack: result.hasAudioTrack,
            hasVideoTrack: result.hasVideoTrack,
        });
        this.applyEvent({
            type: CET.MEDIA_SELECTED_DEVICES_UPDATED,
            selectedDevices: {
                ...this.state.media.selectedDevices,
                audioInputDeviceId: deviceId,
            },
        });
        const syncResult = await this.syncPeerConnectionLocalMedia();
        if (!syncResult.ok) {
            return syncResult;
        }
        return { ok: true };
    }
    async handleSelectVideoInputDevice(deviceId) {
        const result = await this.mediaAdapter.selectVideoInputDevice(deviceId);
        if (!result.ok) {
            this.applyEvent({
                type: CET.MEDIA_ACQUIRE_FAILED,
                payload: result.error,
            });
            return { ok: false, error: result.error.message };
        }
        this.applyEvent({
            type: CET.MEDIA_ACQUIRE_SUCCEEDED,
            hasAudioTrack: result.hasAudioTrack,
            hasVideoTrack: result.hasVideoTrack,
        });
        this.applyEvent({
            type: CET.MEDIA_SELECTED_DEVICES_UPDATED,
            selectedDevices: {
                ...this.state.media.selectedDevices,
                videoInputDeviceId: deviceId,
            },
        });
        const syncResult = await this.syncPeerConnectionLocalMedia();
        if (!syncResult.ok) {
            return syncResult;
        }
        return { ok: true };
    }
    async syncPeerConnectionLocalMedia() {
        if (!this.peerConnectionAdapter.hasPeerConnection()) {
            return { ok: true };
        }
        const attachRes = await this.peerConnectionAdapter.attachLocalMedia();
        if (!attachRes.ok) {
            this.applyEvent({
                type: CET.RTC_ERROR_OCCURRED,
                payload: attachRes.error,
            });
            return { ok: false, error: attachRes.error.message };
        }
        return { ok: true };
    }
    async handleRefreshMediaDevices() {
        const devices = await this.mediaAdapter.enumerateDevices();
        this.applyEvent({
            type: CET.MEDIA_DEVICES_UPDATED,
            devices,
        });
        return { ok: true };
    }
    async handleEnsurePeerConnection(expectedCallId) {
        if (!this.rtcConfigProvider) {
            return { ok: false, error: "Missing rtcConfigProvider" };
        }
        const callIdAtStart = expectedCallId ?? selectActiveCallId(this.state);
        const rtcConfig = await this.rtcConfigProvider();
        if (callIdAtStart !== null && callIdAtStart !== undefined) {
            const currentCallId = selectActiveCallId(this.state);
            if (currentCallId !== callIdAtStart) {
                return {
                    ok: false,
                    error: `Stale peer connection setup for call ${callIdAtStart}`,
                };
            }
        }
        await this.peerConnectionAdapter.ensurePeerConnection(rtcConfig);
        if (callIdAtStart !== null && callIdAtStart !== undefined) {
            const currentCallId = selectActiveCallId(this.state);
            if (currentCallId !== callIdAtStart) {
                await this.peerConnectionAdapter.closePeerConnection();
                return {
                    ok: false,
                    error: `Peer connection setup invalidated by call change for ${callIdAtStart}`,
                };
            }
        }
        return { ok: true };
    }
    async handleCreateAndSendOffer(callId) {
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `No active call ${callId} for offer creation` };
        }
        const ensureRes = await this.handleEnsurePeerConnection(callId);
        if (!ensureRes.ok)
            return ensureRes;
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `Call ${callId} is no longer active` };
        }
        const attachRes = await this.peerConnectionAdapter.attachLocalMedia();
        if (!attachRes.ok)
            return { ok: false, error: attachRes.error.message };
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `Call ${callId} is no longer active` };
        }
        this.applyEvent({
            type: CET.RTC_MAKING_OFFER_CHANGED,
            isMakingOffer: true,
        });
        const offerRes = await this.peerConnectionAdapter.createAndSetLocalOffer();
        this.applyEvent({
            type: CET.RTC_MAKING_OFFER_CHANGED,
            isMakingOffer: false,
        });
        if (!offerRes.ok) {
            this.applyEvent({
                type: CET.RTC_ERROR_OCCURRED,
                payload: offerRes.error,
            });
            return { ok: false, error: offerRes.error.message };
        }
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `Call ${callId} is no longer active` };
        }
        this.applyEvent({ type: CET.RTC_LOCAL_DESCRIPTION_SET });
        return await this.sendFrame(makeSdpOfferFrame({
            ...this.makeBaseOutboundFrameInput(),
            callId,
            sdp: offerRes.sdp,
        }));
    }
    async handleApplyRemoteOfferAndSendAnswer(callId, remoteSdp) {
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `No active call ${callId} for remote offer` };
        }
        const ensureRes = await this.handleEnsurePeerConnection(callId);
        if (!ensureRes.ok)
            return ensureRes;
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `Call ${callId} is no longer active` };
        }
        const attachRes = await this.peerConnectionAdapter.attachLocalMedia();
        if (!attachRes.ok)
            return { ok: false, error: attachRes.error.message };
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `Call ${callId} is no longer active` };
        }
        const answerRes = await this.peerConnectionAdapter.applyRemoteOfferAndCreateAnswer(remoteSdp);
        if (!answerRes.ok) {
            this.applyEvent({
                type: CET.RTC_ERROR_OCCURRED,
                payload: answerRes.error,
            });
            return { ok: false, error: answerRes.error.message };
        }
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `Call ${callId} is no longer active` };
        }
        this.applyEvent({ type: CET.RTC_REMOTE_DESCRIPTION_SET });
        this.applyEvent({ type: CET.RTC_LOCAL_DESCRIPTION_SET });
        await this.flushPendingRemoteIceCandidates(callId);
        return await this.sendFrame(makeSdpAnswerFrame({
            ...this.makeBaseOutboundFrameInput(),
            callId,
            sdp: answerRes.sdp,
        }));
    }
    async handleApplyRemoteAnswer(callId, remoteSdp) {
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `No active call ${callId} for remote answer` };
        }
        this.applyEvent({
            type: CET.RTC_SETTING_REMOTE_ANSWER_PENDING_CHANGED,
            isSettingRemoteAnswerPending: true,
        });
        const result = await this.peerConnectionAdapter.applyRemoteAnswer(remoteSdp);
        this.applyEvent({
            type: CET.RTC_SETTING_REMOTE_ANSWER_PENDING_CHANGED,
            isSettingRemoteAnswerPending: false,
        });
        if (!result.ok) {
            this.applyEvent({
                type: CET.RTC_ERROR_OCCURRED,
                payload: result.error,
            });
            return { ok: false, error: result.error.message };
        }
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `Call ${callId} is no longer active` };
        }
        this.applyEvent({ type: CET.RTC_REMOTE_DESCRIPTION_SET });
        await this.flushPendingRemoteIceCandidates(callId);
        return { ok: true };
    }
    async handleAddRemoteIceCandidate(callId, candidate) {
        if (!this.isActiveCall(callId)) {
            return { ok: false, error: `No active call ${callId} for ICE candidate` };
        }
        if (!this.peerConnectionAdapter.hasPeerConnection() ||
            !this.state.rtc.descriptions.hasRemoteDescription) {
            this.enqueuePendingRemoteIceCandidate(callId, candidate);
            return { ok: true };
        }
        const result = await this.peerConnectionAdapter.addRemoteIceCandidate(candidate);
        if (!result.ok) {
            this.enqueuePendingRemoteIceCandidate(callId, candidate);
            return { ok: true };
        }
        return { ok: true };
    }
    enqueuePendingRemoteIceCandidate(callId, candidate) {
        const queue = this.pendingRemoteIceByCallId.get(callId) ?? [];
        queue.push(candidate);
        this.pendingRemoteIceByCallId.set(callId, queue);
    }
    async flushPendingRemoteIceCandidates(callId) {
        const queue = this.pendingRemoteIceByCallId.get(callId);
        if (!queue || queue.length === 0) {
            return;
        }
        this.pendingRemoteIceByCallId.delete(callId);
        for (const candidate of queue) {
            const result = await this.peerConnectionAdapter.addRemoteIceCandidate(candidate);
            if (!result.ok) {
                this.enqueuePendingRemoteIceCandidate(callId, candidate);
                return;
            }
        }
    }
    isActiveCall(callId) {
        return selectActiveCallId(this.state) === callId;
    }
    makeBaseOutboundFrameInput() {
        const allocated = allocateOutboundFrameMeta({
            runtime: this.runtime,
            protocolVersion: this.protocolVersion,
            now: this.now(),
            msgId: this.makeMsgId(),
        });
        this.runtime = allocated.runtime;
        return allocated.meta;
    }
    async sendFrame(frame) {
        this.appendWsMessageLog("out", frame);
        const result = await this.signalingAdapter.send(frame);
        if (!result.ok) {
            return { ok: false, error: result.error };
        }
        return { ok: true };
    }
    emitState() {
        for (const listener of this.stateListeners) {
            listener(this.state);
        }
    }
    emitEvent(event) {
        for (const listener of this.eventListeners) {
            listener(event, this.state);
        }
    }
    appendWsMessageLog(direction, frame) {
        this.wsMessageLog = [
            ...this.wsMessageLog,
            {
                direction,
                text: JSON.stringify(frame),
            },
        ];
        const snapshot = [...this.wsMessageLog];
        for (const listener of this.wsMessageLogListeners) {
            listener(snapshot);
        }
    }
}
/**
 * createCallController
 * --------------------
 *
 * Convenience factory.
 */
export function createCallController(deps) {
    return new DefaultCallController(deps);
}
